// src/pages/SuperAdmin/components/ContractTabContent.tsx - Onglet Contrat (mensualité, paiements, relances)

import React, { useState, useRef } from 'react';
import {
  Button,
  Card,
  Tag,
  Typography,
  Row,
  Col,
  Empty,
  Spin,
  message,
  Popconfirm,
  Select,
} from 'antd';
import { DollarOutlined, EditOutlined, DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useAttachmentPreview } from '../../../contexts/AttachmentPreviewContext';
import { uploadContractPaymentProof } from '../../../hooks/useExpenseAttachments';
import {
  useBusinessContract,
  useBusinessContractPayments,
  useDeleteContractPayment,
  useUpdateContractPaymentStatus,
} from '../../../hooks/useBusinessContracts';
import { getCoverageExpiryDate, isContractExpiringSoon, getEffectiveContractStatus, hasPaymentDelay, hasPendingOtherPayments, formatContractPaymentCoverageLabel, formatContractPaymentTitle, getCoverageMonthCount, getContractPaymentDiscountInfo } from '../../../types/businessContract';
import EditContractModal from './EditContractModal';
import RecordPaymentModal from './RecordPaymentModal';
import type { Business } from '../../../types/business';
import type { BusinessContractPayment, ContractPaymentStatus } from '../../../types/businessContract';
import dayjs from 'dayjs';
import { formatNumber } from '../../../utils/numberFormat';
import './ContractTabContent.css';

const { Text } = Typography;

const formatMonth = (d: Date) => dayjs(d).format('MM/YYYY');

interface ContractTabContentProps {
  business: Business;
  onRefetch?: () => void;
}

const ContractTabContent: React.FC<ContractTabContentProps> = ({ business, onRefetch }) => {
  const { data: contract, isLoading: contractLoading, refetch: refetchContract } = useBusinessContract(business.id);
  const { data: payments, isLoading: paymentsLoading, refetch: refetchPayments } = useBusinessContractPayments(business.id);
  const deletePaymentMutation = useDeleteContractPayment();
  const updatePaymentStatusMutation = useUpdateContractPaymentStatus();
  const [editContractOpen, setEditContractOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState<BusinessContractPayment | null>(null);
  const [uploadingForPaymentId, setUploadingForPaymentId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paymentToUploadRef = useRef<BusinessContractPayment | null>(null);
  const openAttachmentPreview = useAttachmentPreview();

  const handleSuccess = () => {
    refetchContract();
    refetchPayments();
    onRefetch?.();
  };

  const coverageExpiry = payments && payments.length > 0 ? getCoverageExpiryDate(payments) : null;
  const contractExpiringSoon = contract ? isContractExpiringSoon(contract, 60) : false;
  const paymentDelay = contract && payments ? hasPaymentDelay(payments) : false;
  const pendingOtherPayments = contract && payments ? hasPendingOtherPayments(payments) : false;

  if (contractLoading || paymentsLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderPaymentType = (_: string, r: BusinessContractPayment) => {
    if (r.paymentType === 'monthly') {
      const count = getCoverageMonthCount(r);
      return (
        <Tag color="blue">
          {count > 1 ? `Bloc ${count} mois` : 'Mensualité'}
        </Tag>
      );
    }
    if (r.paymentType === 'complimentary') {
      const count = getCoverageMonthCount(r);
      return (
        <Tag color="green">
          {count > 1
            ? `${r.feeLabel ?? 'Mois offert'} (${count} mois)`
            : (r.feeLabel ?? 'Mois offert')}
        </Tag>
      );
    }
    return <Tag>{r.feeLabel ?? 'Autre frais'}</Tag>;
  };
  const renderCoverage = (_: unknown, r: BusinessContractPayment) =>
    formatContractPaymentCoverageLabel(r, formatMonth);
  const handleDeletePayment = async (payment: BusinessContractPayment) => {
    try {
      await deletePaymentMutation.mutateAsync({ paymentId: payment.id, businessId: business.id });
      message.success('Paiement supprimé');
      handleSuccess();
    } catch {
      message.error('Erreur lors de la suppression');
    }
  };

  const handleUpdatePaymentStatus = async (payment: BusinessContractPayment, status: ContractPaymentStatus) => {
    try {
      await updatePaymentStatusMutation.mutateAsync({
        paymentId: payment.id,
        businessId: business.id,
        status,
        paymentDate: status === 'paid' ? new Date() : undefined,
      });
      message.success('Statut du paiement mis à jour');
      handleSuccess();
    } catch {
      message.error('Erreur lors de la mise à jour du statut');
    }
  };

  const triggerProofUpload = (payment: BusinessContractPayment) => {
    if (payment.paymentType === 'complimentary') return;
    paymentToUploadRef.current = payment;
    fileInputRef.current?.click();
  };

  const handleProofFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const payment = paymentToUploadRef.current;
    e.target.value = '';
    paymentToUploadRef.current = null;
    if (!file || !payment) return;
    setUploadingForPaymentId(payment.id);
    try {
      const url = await uploadContractPaymentProof(business.id, payment.id, file);
      await updatePaymentStatusMutation.mutateAsync({
        paymentId: payment.id,
        businessId: business.id,
        status: payment.status,
        attachmentUrl: url,
      });
      message.success('Justificatif enregistré');
      handleSuccess();
    } catch {
      message.error('Erreur lors de l\'envoi du justificatif');
    } finally {
      setUploadingForPaymentId(null);
    }
  };

  const renderPaymentStatus = (_: unknown, record: BusinessContractPayment) => {
    if (record.paymentType === 'complimentary') {
      return <Tag color="green">Payé</Tag>;
    }
    return (
      <Select
        size="small"
        value={record.status}
        onChange={(value) => handleUpdatePaymentStatus(record, value as ContractPaymentStatus)}
        style={{ width: 130 }}
        options={[
          { value: 'pending', label: 'En attente' },
          { value: 'paid', label: 'Payé' },
          { value: 'cancelled', label: 'Annulé' },
        ]}
      />
    );
  };

  const renderJustificatif = (_: unknown, record: BusinessContractPayment) => {
    if (record.paymentType === 'complimentary') return '—';
    const loading = uploadingForPaymentId === record.id;
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {record.attachmentUrl ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => openAttachmentPreview?.(record.attachmentUrl!)}
          >
            Voir
          </Button>
        ) : null}
        <Button
          type="link"
          size="small"
          icon={<PaperClipOutlined />}
          loading={loading}
          onClick={() => triggerProofUpload(record)}
          style={{ padding: '0 4px' }}
          title="Joindre un justificatif"
        />
      </span>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <Button type="primary" icon={<EditOutlined />} onClick={() => setEditContractOpen(true)}>
          {contract ? 'Modifier le contrat' : 'Créer le contrat'}
        </Button>
        <Button
          icon={<DollarOutlined />}
          onClick={() => {
            setPaymentToEdit(null);
            setRecordPaymentOpen(true);
          }}
          disabled={!contract}
          title={!contract ? 'Créez d\'abord un contrat pour créer une échéance' : undefined}
        >
          Créer une échéance
        </Button>
      </div>

      {contract ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Text type="secondary">Date début</Text>
              <div><Text strong>{dayjs(contract.startDate).format('DD/MM/YYYY')}</Text></div>
            </Col>
            <Col span={6}>
              <Text type="secondary">Date fin (engagement)</Text>
              <div><Text strong>{dayjs(contract.endDate).format('DD/MM/YYYY')}</Text></div>
            </Col>
            <Col span={6}>
              <Text type="secondary">Mensualité</Text>
              <div><Text strong>{formatNumber(contract.monthlyAmount, 0)} FCFA</Text></div>
            </Col>
            <Col span={6}>
              <Text type="secondary">Statut</Text>
              <div>
                <Tag color={getEffectiveContractStatus(contract) === 'active' ? 'green' : getEffectiveContractStatus(contract) === 'expired' ? 'red' : 'orange'}>
                  {getEffectiveContractStatus(contract) === 'active' ? 'Actif' : getEffectiveContractStatus(contract) === 'expired' ? 'Expiré' : 'Renouvellement à prévoir'}
                </Tag>
              </div>
            </Col>
            {contract.description && (
              <Col span={24}>
                <Text type="secondary">Description</Text>
                <div><Text>{contract.description}</Text></div>
              </Col>
            )}
            <Col span={24}>
              <Text type="secondary">Statut des paiements</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {paymentDelay ? (
                  <>
                    <Tag color="red">Retard de paiement</Tag>
                    {coverageExpiry ? (
                      <Text type="secondary">Dernière couverture jusqu'au {dayjs(coverageExpiry).format('DD/MM/YYYY')} (expirée)</Text>
                    ) : (
                      <Text type="secondary">Aucun paiement enregistré pour couvrir les mois en cours</Text>
                    )}
                  </>
                ) : (
                  <>
                    <Text strong style={{ color: contractExpiringSoon ? '#faad14' : '#52c41a' }}>
                      Dernier mois couvert : {coverageExpiry ? dayjs(coverageExpiry).format('MM/YYYY') : '—'}
                    </Text>
                    {contractExpiringSoon && <Tag color="orange">Contrat à renouveler bientôt</Tag>}
                  </>
                )}
                {pendingOtherPayments && (
                  <Tag color="orange">Frais en attente de règlement</Tag>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      ) : (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Empty description="Aucun contrat configuré" />
        </Card>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        aria-label="Joindre un justificatif de paiement"
        style={{ display: 'none' }}
        onChange={handleProofFileSelect}
      />
      <Card title="Échéances et paiements" className="payments-card">
        {payments && payments.length > 0 ? (
          <div className="payments-list">
            {payments.map((payment) => (
              <Card
                key={payment.id}
                className="payment-card"
                size="small"
                style={{
                  marginBottom: 12,
                  borderLeft: `4px solid ${
                    payment.status === 'paid' ? '#52c41a' : 
                    payment.status === 'cancelled' ? '#ff4d4f' : '#fa8c16'
                  }`
                }}
              >
                <Row gutter={[16, 8]} align="middle">
                  <Col xs={24} sm={12} md={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {payment.status === 'paid' ? 'Date de paiement' : 'Échéance'}
                    </Text>
                    <div>
                      <Text strong>
                        {payment.status === 'paid' && payment.paymentDate
                          ? dayjs(payment.paymentDate).format('DD/MM/YYYY')
                          : payment.coverageStartDate
                            ? formatContractPaymentCoverageLabel(payment, formatMonth)
                            : '—'}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Type</Text>
                    <div>{renderPaymentType('', payment)}</div>
                    {getCoverageMonthCount(payment) > 1 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatContractPaymentTitle(payment, formatMonth)}
                      </Text>
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Statut</Text>
                    <div>{renderPaymentStatus('', payment)}</div>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Montant</Text>
                    <div>
                      {(() => {
                        const discount = getContractPaymentDiscountInfo(payment);
                        if (!discount.hasDiscount) {
                          return (
                            <Text strong style={{ color: '#1890ff' }}>
                              {formatNumber(payment.amount ?? 0, 0)} FCFA
                            </Text>
                          );
                        }
                        return (
                          <div>
                            <Text delete type="secondary" style={{ fontSize: 12 }}>
                              {formatNumber(discount.originalAmount, 0)} FCFA
                            </Text>
                            <div>
                              <Text strong style={{ color: '#d46b08' }}>
                                {formatNumber(discount.amount, 0)} FCFA
                              </Text>
                            </div>
                            <Tag color="orange" style={{ marginTop: 4 }}>
                              {discount.discountLabel}
                              {discount.discountPercent != null
                                ? ` −${discount.discountPercent}%`
                                : ''}
                            </Tag>
                          </div>
                        );
                      })()}
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Période couverte</Text>
                    <div><Text>{renderCoverage('', payment)}</Text></div>
                  </Col>
                  <Col xs={12} sm={6} md={2}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Justificatif</Text>
                    <div>{renderJustificatif('', payment)}</div>
                  </Col>
                  <Col xs={12} sm={6} md={2} style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 0 }}>
                      {payment.status !== 'paid' && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          title="Modifier l’échéance"
                          onClick={() => {
                            setPaymentToEdit(payment);
                            setRecordPaymentOpen(true);
                          }}
                        />
                      )}
                      <Popconfirm
                        title="Supprimer cette échéance ?"
                        description="Cette action est irréversible."
                        onConfirm={() => handleDeletePayment(payment)}
                        okText="Supprimer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                      </Popconfirm>
                    </div>
                  </Col>
                </Row>
                {payment.reference && (
                  <Row style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Référence: </Text>
                      <Text code style={{ fontSize: 12 }}>{payment.reference}</Text>
                    </Col>
                  </Row>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <Empty description="Aucune échéance enregistrée" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <EditContractModal
        open={editContractOpen}
        business={business}
        contract={contract ?? null}
        onClose={() => setEditContractOpen(false)}
        onSuccess={handleSuccess}
      />
      <RecordPaymentModal
        open={recordPaymentOpen}
        business={business}
        contract={contract ?? null}
        paymentToEdit={paymentToEdit}
        onClose={() => {
          setRecordPaymentOpen(false);
          setPaymentToEdit(null);
        }}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default ContractTabContent;
